import type { Imovel } from '@/integrations/supabase/types';
import type { PropertyCardProps } from '@/components/PropertyCard';
import { getFotosUrls } from '@/utils/fotoHelpers';

/**
 * Formata valor monetário para R$ X.XXX,XX
 */
export function formatCurrency(value: number | null | undefined): string {
  if (!value) return 'R$ 0';
  
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Constrói string de custos adicionais (condomínio + IPTU)
 */
export function buildAdditionalCosts(condominio?: number | null, iptu?: number | null): string | undefined {
  const parts: string[] = [];
  
  if (condominio && condominio > 0) {
    parts.push(`Condomínio ${formatCurrency(condominio)}`);
  }
  
  if (iptu && iptu > 0) {
    parts.push(`IPTU ${formatCurrency(iptu)}`);
  }
  
  return parts.length > 0 ? parts.join(' + ') : undefined;
}

/**
 * Constrói string de atributos do imóvel (quartos, banheiros, área)
 */
export function buildAttributes(imovel: Imovel): string {
  const parts: string[] = [];
  
  if (imovel.quartos) {
    parts.push(`${imovel.quartos} ${imovel.quartos === 1 ? 'quarto' : 'quartos'}`);
  }
  
  if (imovel.banheiros) {
    parts.push(`${imovel.banheiros} ${imovel.banheiros === 1 ? 'banheiro' : 'banheiros'}`);
  }
  
  if (imovel.area) {
    parts.push(`${imovel.area}m²`);
  }
  
  return parts.join(' • ') || 'Consulte';
}

/**
 * Verifica se o imóvel é novo (criado nos últimos 7 dias)
 */
export function isNewProperty(createdAt?: string): boolean {
  if (!createdAt) return false;
  
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  
  return diffDays <= 7;
}

/**
 * Mapeia o tipo de imóvel do HouseBit para label amigável
 */
export function mapPropertyType(tipo: string): string {
  const typeMap: Record<string, string> = {
    'apartamento': 'Apartamento',
    'casa': 'Casa',
    'comercial': 'Comercial',
    'terreno': 'Terreno',
    'rural': 'Rural',
    'studio': 'Studio',
    'cobertura': 'Cobertura',
    'sobrado': 'Sobrado',
    'kitnet': 'Kitnet',
  };
  
  return typeMap[tipo] || tipo;
}

/**
 * Constrói endereço completo a partir dos campos individuais
 * Fallback para quando endereco_completo estiver vazio
 */
export function buildFullAddress(imovel: Imovel): string {
  // Se endereco_completo já existe e não está vazio, usa ele
  if (imovel.endereco_completo && imovel.endereco_completo.trim() !== '') {
    return imovel.endereco_completo;
  }
  
  // Caso contrário, constrói a partir dos campos individuais
  const parts: string[] = [];
  
  // Logradouro + número
  if (imovel.endereco_logradouro) {
    let endereco = imovel.endereco_logradouro;
    if (imovel.endereco_numero) {
      endereco += `, ${imovel.endereco_numero}`;
    }
    parts.push(endereco);
  }
  
  // Bairro
  if (imovel.endereco_bairro) {
    parts.push(imovel.endereco_bairro);
  }
  
  // Cidade - UF
  if (imovel.endereco_cidade && imovel.endereco_uf) {
    parts.push(`${imovel.endereco_cidade} - ${imovel.endereco_uf}`);
  } else if (imovel.endereco_cidade) {
    parts.push(imovel.endereco_cidade);
  }
  
  return parts.join(', ') || 'Endereço não informado';
}

/**
 * Converte imóvel do HouseBit para formato PropertyCard
 */
export function imovelToPropertyCard(imovel: Imovel): PropertyCardProps {
  // Determinar preço principal baseado na finalidade
  const isRental = imovel.finalidade.includes('locacao');
  const isSale = imovel.finalidade.includes('venda');
  
  // Construir objeto de preços para a nova estrutura
  const prices: PropertyCardProps['prices'] = {};
  
  // Adicionar preço de venda se disponível
  if (isSale && imovel.valor_venda) {
    prices.sale = {
      value: formatCurrency(imovel.valor_venda),
      label: 'Venda',
      condominium: imovel.valor_condominio && imovel.valor_condominio > 0 
        ? formatCurrency(imovel.valor_condominio) 
        : undefined,
      iptu: imovel.valor_iptu && imovel.valor_iptu > 0 
        ? formatCurrency(imovel.valor_iptu) 
        : undefined,
    };
  }
  
  // Adicionar preço de locação se disponível
  if (isRental && imovel.valor_locacao) {
    prices.rental = {
      value: formatCurrency(imovel.valor_locacao),
      unit: '/mês',
      label: 'Aluguel',
      condominium: imovel.valor_condominio && imovel.valor_condominio > 0 
        ? formatCurrency(imovel.valor_condominio) 
        : undefined,
      iptu: imovel.valor_iptu && imovel.valor_iptu > 0 
        ? formatCurrency(imovel.valor_iptu) 
        : undefined,
    };
  }
  
  // Determinar saleType principal (para compatibilidade)
  // Priorizar venda se tiver ambos
  const mainSaleType: 'Locação' | 'Venda' = 
    (isSale && prices.sale) ? 'Venda' : 'Locação';
  
  const mainPrice = mainSaleType === 'Venda' 
    ? prices.sale?.value 
    : prices.rental?.value;
  
  // Extrair URLs das fotos usando helper (suporta todos os formatos + marca d'água)
  const imageUrls = getFotosUrls(
    imovel.fotos, 
    imovel.marca_dagua || false
  );
  
  // Fallback para imagem padrão se não houver fotos
  const finalImageUrls = imageUrls.length > 0 ? imageUrls : ['/img-default.jpg'];
  
  return {
    id: imovel.id,
    title: imovel.titulo,
    code: imovel.codigo,
    type: mapPropertyType(imovel.tipo),
    attributes: buildAttributes(imovel),
    
    // Nova estrutura
    prices,
    
    // Compatibilidade (deprecated)
    price: mainPrice || 'R$ 0',
    priceUnit: mainSaleType === 'Locação' ? '/mês' : undefined,
    saleType: mainSaleType,
    additionalCosts: mainSaleType === 'Locação' 
      ? buildAdditionalCosts(imovel.valor_condominio, imovel.valor_iptu)
      : undefined,
    
    address: buildFullAddress(imovel),
    image: finalImageUrls,
    isNew: isNewProperty(imovel.created_at),
    engagementCount: imovel.metricas_visualizacoes && imovel.metricas_visualizacoes > 0 ? imovel.metricas_visualizacoes : undefined,
  };
}

/**
 * Converte múltiplos imóveis do HouseBit para formato PropertyCard
 */
export function imoveisToPropertyCards(imoveis: Imovel[]): PropertyCardProps[] {
  return imoveis.map(imovelToPropertyCard);
}
